import * as XLSX from 'xlsx'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'

export interface BrandingOptions {
    logoDataUrl?: string       // PNG data URL of logo (e.g. from svgUrlToPngDataUrl)
    titleLines?: string[]      // Lines of header text to show under logo in PDF / in XLS row
}

export async function svgUrlToPngDataUrl(svgUrl: string, width = 50, height = 52): Promise<string> {
    return new Promise((resolve) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height
            const ctx = canvas.getContext('2d')!
            ctx.drawImage(img, 0, 0, width, height)
            resolve(canvas.toDataURL('image/png'))
        }
        img.onerror = () => resolve('')
        img.src = svgUrl
    })
}

export interface ExportXlsxOptions {
    fileName: string
    sheetName: string
    headers: (string | number)[]
    rows: (string | number | boolean | null | undefined)[][]
    branding?: BrandingOptions
}

export function exportTableToXlsx(options: ExportXlsxOptions): void {
    const { fileName, sheetName, headers, rows, branding } = options
    const brandingRow = branding?.titleLines?.length
        ? [branding.titleLines.join(' | ')]
        : []
    const data = brandingRow.length ? [brandingRow, headers, ...rows] : [headers, ...rows]
    const worksheet = XLSX.utils.aoa_to_sheet(data)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
    XLSX.writeFile(workbook, fileName)
}

export interface ExportPdfOptions {
    fileName: string
    title?: string
    headers: (string | number)[]
    rows: (string | number | boolean | null | undefined)[][]
    startY?: number
    orientation?: 'portrait' | 'landscape'
    columnStyles?: Record<number, { cellWidth?: number | 'auto' | 'wrap'; [key: string]: any }>
    headStyles?: {
        fillColor?: [number, number, number]
        textColor?: number
    }
    styles?: {
        fontSize?: number
        cellPadding?: number | { top?: number; right?: number; bottom?: number; left?: number }
    }
    branding?: BrandingOptions
}

export function exportTableToPdf(options: ExportPdfOptions): void {
    const {
        fileName,
        title,
        headers,
        rows,
        startY,
        orientation = 'portrait',
        columnStyles,
        styles = { fontSize: 10, cellPadding: 3 },
        headStyles = { fillColor: [41, 128, 185], textColor: 255 },
        branding
    } = options

    const doc = new jsPDF({ orientation })
    let currentY = 14

    if (branding?.logoDataUrl) {
        doc.addImage(branding.logoDataUrl, 'PNG', 14, currentY, 13, 13)
        currentY += 17
    }

    if (branding?.titleLines?.length) {
        doc.setFontSize(10)
        doc.setTextColor(100)
        for (const line of branding.titleLines) {
            doc.text(line, 14, currentY)
            currentY += 6
        }
        currentY += 2
    }

    if (title) {
        doc.setFontSize(16)
        doc.setTextColor(0)
        doc.text(title, 14, currentY)
        currentY += 10
    }

    const tableStartY = startY ?? currentY

    autoTable(doc, {
        head: [headers],
        body: rows,
        startY: tableStartY,
        styles,
        headStyles,
        ...(columnStyles ? { columnStyles } : {})
    })

    doc.save(fileName)
}



